import type { UIRouterGlobals } from "@uirouter/angularjs";
import type { IScope } from "angular";
import { merge, Subject } from "rxjs";
import { filter, finalize, switchMap } from "rxjs/operators";

import { intersection, isNil } from "lodash";
import type {
    DirectConnectionType,
    IAvailableChannel,
    IDirectConnection,
    IResponseWithLoading,
    IrKvp,
    IrMap,
    ISiteEquipmentBase,
} from "App/Modules/Models";
import { DirectConnectionTypeFunctions } from "App/Modules/Models";
import { EquipmentClass } from "App/Modules/Models/Models/EquipmentClass";
import type { ISiteEquipmentRepository } from "App/Modules/Repositories/SiteEquipment/ISiteEquipmentRepository";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

enum Mode {
    "Output" = 0,
    "Input" = 1,
}

export default class CreateDirectConnectionComponentController {
    public static $inject: string[] = ["$scope", "$uiRouterGlobals", "mmSiteEquipmentRepository", "$translate"];

    public siteId: string;

    public currentSiteEquipment: ISiteEquipmentBase;

    public connectToSiteEquipment: ISiteEquipmentBase;

    public currentChannel: IAvailableChannel;

    public connectToChannel: IAvailableChannel;

    public directConnectionType: DirectConnectionType;

    public currentChannelOptions: IAvailableChannel[];

    public connectToSiteEquipmentOptions: ISiteEquipmentBase[];

    public connectToChannelOptions: IAvailableChannel[];

    public connectToChannelAllOptions: IAvailableChannel[];

    public directConnectionTypeOptions: IrMap<DirectConnectionType, string>;

    public resolve: IFormContainerResolve<ISiteEquipmentBase, null, { dc: IDirectConnection; ac: IAvailableChannel }>;

    public createDirectConnectionResponse: IResponseWithLoading<IDirectConnection>;

    public getAvailableConnectToChannelResponse: IResponseWithLoading<IAvailableChannel[]>;

    public getSiteEquipmentResponse: IResponseWithLoading<ISiteEquipmentBase[]>;

    private currentChannelChangeSubject: Subject<IAvailableChannel>;

    private connectToChannelChangeSubject: Subject<IAvailableChannel>;

    private connectToSiteEquipmentChangeSubject: Subject<ISiteEquipmentBase>;

    private connectToChannelAllOptionsChangeSubject: Subject<IAvailableChannel[]>;

    constructor(
        private readonly $scope: IScope,
        private readonly $uiRouterGlobals: UIRouterGlobals,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public $onInit() {
        this.currentChannelChangeSubject = new Subject<IAvailableChannel>();
        this.connectToChannelChangeSubject = new Subject<IAvailableChannel>();
        this.connectToSiteEquipmentChangeSubject = new Subject<ISiteEquipmentBase>();
        this.connectToChannelAllOptionsChangeSubject = new Subject<IAvailableChannel[]>();

        merge(this.currentChannelChangeSubject, this.connectToChannelAllOptionsChangeSubject).subscribe(() => {
            if (isNil(this.connectToChannelAllOptions)) {
                return;
            }

            this.connectToChannelOptions = this.connectToChannelAllOptions.filter(
                (availableChannel) =>
                    intersection(
                        this.currentChannel.supportedDirectConnections,
                        availableChannel.supportedDirectConnections,
                    ).length > 0,
            );

            if (this.connectToChannelOptions.length > 0) {
                this.connectToChannel = this.connectToChannelOptions[0];
            } else {
                this.connectToChannel = null;
            }
        });

        merge(this.currentChannelChangeSubject, this.connectToChannelChangeSubject).subscribe(() => {
            if (!this.connectToChannel || !this.currentChannel) {
                return;
            }

            this.directConnectionTypeOptions = DirectConnectionTypeFunctions.GetDirectConnectionTypeDictionary(
                this.$translate,
            ).filter((type: IrKvp<DirectConnectionType, string>) => {
                const inputSupports = this.connectToChannel.supportedDirectConnections.some(
                    (supportedType) => supportedType === type.key,
                );

                const outputsSupports = this.currentChannel.supportedDirectConnections.some(
                    (supportedType) => supportedType === type.key,
                );

                return inputSupports && outputsSupports;
            });

            if (this.directConnectionTypeOptions.length >= 1) {
                this.directConnectionType = this.directConnectionTypeOptions[0].key;
            } else {
                this.directConnectionType = null;
            }
        });

        this.connectToSiteEquipmentChangeSubject
            .pipe(
                filter((siteEquipment: ISiteEquipmentBase) => siteEquipment !== undefined),
                switchMap(() =>
                    extendWithLoadingAndFail(
                        this.siteEquipmentRepository.getAvailableChannels(this.siteId, this.connectToSiteEquipment.id),
                    ),
                ),
                finalize(() => this.$scope.$applyAsync()),
            )
            .subscribe(
                (response: IResponseWithLoading<IAvailableChannel[]>) => {
                    this.getAvailableConnectToChannelResponse = response;

                    if (!response.isLoading && response.data) {
                        this.connectToChannelAllOptions = response.data;

                        this.currentChannel = this.getFirstChannelWithPotentialConnectionOptions();
                    }
                },
                (error) => {
                    this.getAvailableConnectToChannelResponse = error;
                },
            );

        this.$scope.$watch("$ctrl.currentChannel", (newValue: IAvailableChannel) =>
            this.currentChannelChangeSubject.next(newValue),
        );

        this.$scope.$watch("$ctrl.connectToChannel", (newValue: IAvailableChannel) =>
            this.connectToChannelChangeSubject.next(newValue),
        );

        this.$scope.$watch("$ctrl.connectToSiteEquipment", (newValue: ISiteEquipmentBase) =>
            this.connectToSiteEquipmentChangeSubject.next(newValue),
        );

        this.$scope.$watch("$ctrl.connectToChannelAllOptions", (newValue: IAvailableChannel[]) =>
            this.connectToChannelAllOptionsChangeSubject.next(newValue),
        );

        this.siteId = this.$uiRouterGlobals.params.siteId;
        this.currentSiteEquipment = this.resolve.formData;
        this.currentChannelOptions = this.resolve.bindings;

        extendWithLoadingAndFail(this.siteEquipmentRepository.getForSiteSlim(this.siteId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<ISiteEquipmentBase[]>) => {
                    this.getSiteEquipmentResponse = response;

                    if (!response.isLoading && response.data) {
                        this.connectToSiteEquipmentOptions = response.data.filter(
                            (eq: ISiteEquipmentBase) =>
                                eq.id !== this.currentSiteEquipment.id && eq.type !== null && eq.type !== undefined,
                        );
                    }
                },
                (error) => {
                    this.getSiteEquipmentResponse = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public createDirectConnection() {
        if (isNil(this.directConnectionType) || isNil(this.connectToChannel)) {
            return;
        }

        let postObject: IDirectConnection;

        if (this.getMode() === Mode.Input) {
            postObject = {
                directConnectionType: this.directConnectionType,
                inputChannel: this.currentChannel,
                inputSiteEquipment: this.currentSiteEquipment,
                outputChannel: this.connectToChannel,
                outputSiteEquipment: this.connectToSiteEquipment,
            } as IDirectConnection;
        } else if (this.getMode() === Mode.Output) {
            postObject = {
                directConnectionType: this.directConnectionType,
                inputChannel: this.connectToChannel,
                inputSiteEquipment: this.connectToSiteEquipment,
                outputChannel: this.currentChannel,
                outputSiteEquipment: this.currentSiteEquipment,
            } as IDirectConnection;
        }

        extendWithLoadingAndFail(this.siteEquipmentRepository.createDirectConnection(this.siteId, postObject))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response: IResponseWithLoading<IDirectConnection>) => {
                    this.createDirectConnectionResponse = response;

                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk({
                            ac: this.currentChannel,
                            dc: response.data,
                        });
                    }
                },
                (error) => {
                    this.createDirectConnectionResponse = error;
                },
            );
    }

    private getMode() {
        switch (this.currentSiteEquipment.class) {
            case EquipmentClass.IOModule:
            case EquipmentClass.AnalogCamera:
                return Mode.Output;
            case EquipmentClass.Siren:
            case EquipmentClass.GenericOutput:
            case EquipmentClass.Flashlight:
            case EquipmentClass.Speaker:
            case EquipmentClass.VideoConverter:
                return Mode.Input;
            default:
                return Mode.Input;
        }
    }

    private getFirstChannelWithPotentialConnectionOptions() {
        const channelWithConnections = this.currentChannelOptions.find((currentChannel) =>
            this.connectToChannelAllOptions.some(
                (connectToChannel) =>
                    intersection(currentChannel.supportedDirectConnections, connectToChannel.supportedDirectConnections)
                        .length > 0,
            ),
        );

        return isNil(channelWithConnections) ? this.currentChannelOptions[0] : channelWithConnections;
    }
}
